/**
 * TRANSLATION STATUS AGENT — single source of truth for the menu's translation state.
 *
 * Pure, deterministic, side-effect free. No React, no DB, no AI. Given a menu and the set of
 * enabled languages, it answers precisely: what is translated, what is missing, where, and WHY
 * (missing language / empty field / source echoed / source changed after last translation).
 *
 * It is the brain that tells the other parts (Translate-all, status badges, AddDishes) what to do.
 *
 * WHY a source hash: to detect when the source name/description/variant labels changed AFTER a
 * translation was made (so that translation is now stale). The hash is stored inside the existing
 * `translations[lang]` JSON under the reserved key `_srcHash` — NO new DB column (same approach as
 * BASE_VARIANT_LABEL_ID). Languages that only ever need "missing/empty" detection work without it.
 *
 * DB SAFETY (verified): the constraint validate_menu_item_translations only requires that each
 * translations[lang] has string `name` and `description` — extra keys (variantLabels, _srcHash) are
 * allowed. _srcHash lives INSIDE translations[lang], never as a top-level "language" key (that would
 * fail is_valid_language_code). getEffectiveTranslation SQL reads only name/description, ignores it.
 */

import type { MenuCategory, MenuItem } from '../types';

/** The 5 base languages always present. Mirrors menu-engine BASE_LANGS. */
export const BASE_LANGS = ['it', 'es', 'en', 'fr', 'de'] as const;

/** Reserved key stored inside translations[lang] to track the source state at translation time. */
export const SOURCE_HASH_KEY = '_srcHash';

/** Why a (item, lang) unit is considered untranslated. */
export type MissingReason = 'missing_lang' | 'empty_name' | 'echoed_source' | 'source_changed';

export interface ItemLangStatus {
    lang: string;
    complete: boolean;
    /** Present only when not complete. */
    reason?: MissingReason;
}

export interface ItemStatus {
    itemId: string;
    /** Languages still needing work, with the reason. */
    missing: Array<{ lang: string; reason: MissingReason }>;
}

export interface MenuTranslationStatus {
    /** Total target languages considered. */
    languages: string[];
    /** Per-language count of items needing work. */
    missingByLang: Record<string, number>;
    /** Items needing work (only those with at least one missing lang). */
    items: ItemStatus[];
    /** Categories whose categoryName needs work, per language. */
    categoriesMissingByLang: Record<string, number>;
    /** Grand total of (unit × lang) slices to translate — for cost estimation. */
    totalMissingSlices: number;
    /** True when nothing needs translation. */
    fullyTranslated: boolean;
}

// ─────────────────────────────────────────────────────────────────
// Language resolution
// ─────────────────────────────────────────────────────────────────

/** Full target set: 5 base + de-duplicated valid extras (base first, extras in input order). */
export function resolveTargetLanguages(additionalLanguages?: string[] | null): string[] {
    const base = [...BASE_LANGS] as string[];
    if (!Array.isArray(additionalLanguages) || additionalLanguages.length === 0) return base;
    const extras = additionalLanguages.filter(
        (l, i, arr) => typeof l === 'string' && l && !base.includes(l) && arr.indexOf(l) === i
    );
    return [...base, ...extras];
}

// ─────────────────────────────────────────────────────────────────
// Source hashing (detect "source changed after translation")
// ─────────────────────────────────────────────────────────────────

/** Small, stable, non-crypto string hash (djb2). Deterministic across runs. */
function djb2(str: string): string {
    let h = 5381;
    for (let i = 0; i < str.length; i++) {
        h = ((h << 5) + h + str.charCodeAt(i)) | 0;
    }
    return (h >>> 0).toString(36);
}

/**
 * Canonical source signature for an item: the source-language fields that, if changed, make existing
 * translations stale. We include base name/description and the source variant labels (variant.label),
 * which is what gets translated. Whitespace-normalized so trivial edits don't trigger re-translation.
 */
export function computeItemSourceHash(item: MenuItem): string {
    const parts: string[] = [
        (item.name || '').trim(),
        (item.description || '').trim(),
        (item.basePriceLabel || '').trim(),
    ];
    const variants = Array.isArray(item.variants) ? item.variants : [];
    for (const v of variants) {
        parts.push(`${v.id}:${(v.label || '').trim()}`);
    }
    return djb2(parts.join(''));
}

/** Source signature for a category: its source categoryName. */
export function computeCategorySourceHash(cat: MenuCategory): string {
    return djb2((cat.categoryName || '').trim());
}

// ─────────────────────────────────────────────────────────────────
// Per-item / per-category status
// ─────────────────────────────────────────────────────────────────

type ItemLangTrans = { name?: string; description?: string; variantLabels?: Record<string, string>; [SOURCE_HASH_KEY]?: string };

/**
 * Why (item, lang) is untranslated, or null if complete. Order of checks matters.
 *
 * Source-hash semantics (single source of truth for "already processed"):
 *  - If a stored hash exists and MATCHES the current source → the AI has already processed this item
 *    for this language. The translation is COMPLETE even if the text equals the source name (legitimate
 *    when the dish name is already in the target language, a proper noun, or a brand — e.g. "MEAT LOVER
 *    PEPPERONI" IT→EN). This prevents an infinite "echo → retranslate → echo" loop.
 *  - If a stored hash exists but DIFFERS → the source changed after translation → 'source_changed'.
 *  - If NO hash (legacy/never verified) → fall back to the echo heuristic to catch genuine AI failures.
 */
export function itemLangReason(item: MenuItem, lang: string, sourceLang: string): MissingReason | null {
    // The source language itself is never "missing" — its content lives in the base fields.
    if (lang === sourceLang) return null;

    const tr = (item.translations?.[lang] || undefined) as ItemLangTrans | undefined;
    if (!tr) return 'missing_lang';

    const name = (tr.name || '').trim();
    if (name === '') return 'empty_name';

    const storedHash = tr[SOURCE_HASH_KEY];
    if (storedHash) {
        // Verified by a previous run: complete if the source is unchanged, stale otherwise.
        return storedHash === computeItemSourceHash(item) ? null : 'source_changed';
    }

    // No hash → legacy entry. Use the echo heuristic to flag a verbatim copy of the source name
    // (a likely AI failure on historical data). Once retranslated, the hash stamp resolves it for good.
    if (name.toLowerCase() === (item.name || '').trim().toLowerCase() && (item.name || '').trim() !== '') {
        return 'echoed_source';
    }

    return null;
}

/** Languages still needing work for an item, with reasons. */
export function itemMissingLangs(item: MenuItem, languages: string[], sourceLang: string): Array<{ lang: string; reason: MissingReason }> {
    const out: Array<{ lang: string; reason: MissingReason }> = [];
    for (const lang of languages) {
        const reason = itemLangReason(item, lang, sourceLang);
        if (reason) out.push({ lang, reason });
    }
    return out;
}

/** True when every target language is complete for this item. */
export function isItemFullyTranslated(item: MenuItem, languages: string[], sourceLang: string): boolean {
    return itemMissingLangs(item, languages, sourceLang).length === 0;
}

/** Why a category name is untranslated for a lang, or null. Same hash semantics as itemLangReason. */
export function categoryLangReason(cat: MenuCategory, lang: string, sourceLang: string): MissingReason | null {
    if (lang === sourceLang) return null;
    const tr = cat.translations?.[lang];
    const name = (tr?.categoryName || '').trim();
    if (name === '') return tr ? 'empty_name' : 'missing_lang';

    const storedHash = tr?.[SOURCE_HASH_KEY];
    if (storedHash) {
        return storedHash === computeCategorySourceHash(cat) ? null : 'source_changed';
    }

    // No hash → legacy. Echo heuristic only as fallback.
    if (name.toLowerCase() === (cat.categoryName || '').trim().toLowerCase() && (cat.categoryName || '').trim() !== '') {
        return 'echoed_source';
    }
    return null;
}

// ─────────────────────────────────────────────────────────────────
// Whole-menu status (the agent's main answer)
// ─────────────────────────────────────────────────────────────────

/**
 * Full translation status for the menu. `sourceLang` is the language the base fields are written in
 * (customerPreferences.sourceLanguage, default 'it'); that language is never counted as missing.
 */
export function getMenuTranslationStatus(
    menu: MenuCategory[],
    languages: string[],
    sourceLang = 'it'
): MenuTranslationStatus {
    const missingByLang: Record<string, number> = {};
    const categoriesMissingByLang: Record<string, number> = {};
    const items: ItemStatus[] = [];
    let totalMissingSlices = 0;

    for (const lang of languages) {
        missingByLang[lang] = 0;
        categoriesMissingByLang[lang] = 0;
    }

    for (const cat of menu) {
        for (const lang of languages) {
            if (categoryLangReason(cat, lang, sourceLang)) {
                categoriesMissingByLang[lang]++;
                totalMissingSlices++;
            }
        }
        for (const item of cat.items || []) {
            const missing = itemMissingLangs(item, languages, sourceLang);
            if (missing.length > 0) {
                items.push({ itemId: item.id, missing });
                for (const m of missing) missingByLang[m.lang]++;
                totalMissingSlices += missing.length;
            }
        }
    }

    return {
        languages,
        missingByLang,
        categoriesMissingByLang,
        items,
        totalMissingSlices,
        fullyTranslated: totalMissingSlices === 0,
    };
}

/**
 * Returns ONLY the items that need (re)translation, scoped to the missing languages, for a category.
 * This is what "Translate all" feeds to the AI so it sends only what is needed (not the whole menu).
 * Each returned item carries the subset of languages still missing for it.
 */
export function getUntranslatedItems(
    cat: MenuCategory,
    languages: string[],
    sourceLang = 'it'
): Array<{ item: MenuItem; missingLangs: string[] }> {
    const out: Array<{ item: MenuItem; missingLangs: string[] }> = [];
    for (const item of cat.items || []) {
        const missing = itemMissingLangs(item, languages, sourceLang).map(m => m.lang);
        if (missing.length > 0) out.push({ item, missingLangs: missing });
    }
    return out;
}

/**
 * Stamp the current source hash into a freshly-built translations object for an item, so future runs
 * can detect when the source later changes. Call this right after writing AI translations. Returns a
 * NEW translations object (does not mutate). Only stamps languages present in `translations`.
 */
export function stampSourceHash(
    item: MenuItem,
    translations: NonNullable<MenuItem['translations']>
): NonNullable<MenuItem['translations']> {
    const hash = computeItemSourceHash(item);
    const next: NonNullable<MenuItem['translations']> = {};
    for (const [lang, val] of Object.entries(translations)) {
        next[lang] = { ...(val as ItemLangTrans), [SOURCE_HASH_KEY]: hash } as typeof val;
    }
    return next;
}

/**
 * Stamp the current source hash into a freshly-built category translations object, so future runs
 * can detect when the categoryName later changes. Returns a NEW object (does not mutate).
 */
export function stampCategorySourceHash(
    cat: MenuCategory,
    translations: NonNullable<MenuCategory['translations']>
): NonNullable<MenuCategory['translations']> {
    const hash = computeCategorySourceHash(cat);
    const next: NonNullable<MenuCategory['translations']> = {};
    for (const [lang, val] of Object.entries(translations)) {
        next[lang] = { ...val, [SOURCE_HASH_KEY]: hash };
    }
    return next;
}

// ─────────────────────────────────────────────────────────────────
// Menu completion (dashboard tracker) — pure, parametrized on real settings
// ─────────────────────────────────────────────────────────────────

export interface MenuCompletionResult {
    percentage: number;
    totalItems: number;
    /** Real translation target languages used (5 base + extras − source; empty if monolingual). */
    translationLangs: string[];
    missing: {
        images: string[];
        prices: string[];
        titles: string[];
        descriptions: string[];
        /** Keyed by the real target languages. */
        translations: Record<string, string[]>;
        allergens: string[];
        tags: string[];
    };
}

export interface MenuCompletionSettings {
    sourceLanguage?: string;
    additionalLanguages?: string[] | null;
    /** Default ON (undefined/true). When false, translation points are dropped (monolingual → 100%). */
    multilingualEnabled?: boolean;
}

/**
 * Completion percentage for the dashboard tracker. Pure & parametrized: 6 base points per item
 * (image, price, title, description, allergens, tags) + 1 per translation target language. A language
 * counts as done only when the agent says so (itemLangReason === null → not missing/empty/echoed/stale,
 * echo = NOT done). Monolingual menus (multilingualEnabled === false) get zero translation points so a
 * complete single-language menu reaches 100%. The source language is never a translation target.
 */
export function computeMenuCompletion(menu: MenuCategory[], settings?: MenuCompletionSettings): MenuCompletionResult {
    const sourceLang = settings?.sourceLanguage || 'it';
    const multilingualOn = settings?.multilingualEnabled !== false;
    const translationLangs = multilingualOn
        ? resolveTargetLanguages(settings?.additionalLanguages).filter(l => l !== sourceLang)
        : [];

    const allItems = (menu || []).flatMap(cat => cat.items || []);
    const totalItems = allItems.length;
    const emptyTranslations = (): Record<string, string[]> =>
        Object.fromEntries(translationLangs.map(l => [l, [] as string[]]));

    if (totalItems === 0) {
        return { percentage: 0, totalItems: 0, translationLangs, missing: { images: [], prices: [], titles: [], descriptions: [], translations: emptyTranslations(), allergens: [], tags: [] } };
    }

    const missing = {
        images: [] as string[], prices: [] as string[], titles: [] as string[], descriptions: [] as string[],
        translations: emptyTranslations(), allergens: [] as string[], tags: [] as string[],
    };

    let earned = 0;
    const pointsPerItem = 6 + translationLangs.length;

    for (const item of allItems) {
        const name = (item.name || '').trim();
        if (item.imageUrl && item.imageUrl.trim() !== '') earned++; else missing.images.push(name || item.id);
        if (item.price && item.price.trim() !== '') earned++; else missing.prices.push(name || item.id);
        if (name) earned++; else missing.titles.push(item.id);
        if (item.description?.trim()) earned++; else missing.descriptions.push(name || item.id);
        for (const lang of translationLangs) {
            if (itemLangReason(item, lang, sourceLang) === null) earned++; else missing.translations[lang].push(name || item.id);
        }
        if (item.allergenIds && item.allergenIds.length > 0) earned++; else missing.allergens.push(name || item.id);
        if (item.tagIds && item.tagIds.length > 0) earned++; else missing.tags.push(name || item.id);
    }

    const percentage = Math.round((earned / (totalItems * pointsPerItem)) * 100);
    return { percentage, totalItems, translationLangs, missing };
}
